import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from '../utils/axiosInstance';
import { isoDay } from '../utils/dateRange';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const pad2 = (n) => String(n).padStart(2, '0');
const snap5 = (m) => pad2(Math.floor(m / 5) * 5);
const naiveISO = (dateStr, hour, min) => `${dateStr}T${hour}:${min}:00`;
const fieldsToDate = (dateStr, hour, min) =>
  dateStr ? new Date(`${dateStr}T${hour}:${min}:00`) : null;

const TIME_FMT = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });
const DATE_SHORT_FMT = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
const deTime = (d) => TIME_FMT.format(d);
const deDateShort = (d) => DATE_SHORT_FMT.format(d);

const HOURS_24 = Array.from({ length: 24 }, (_, i) => pad2(i));
const MINUTES_5 = Array.from({ length: 12 }, (_, i) => pad2(i * 5));

const ABS_LABEL_STYLE = { color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: 500, minWidth: 32 };
const ABS_GROUP_STYLE = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  border: '1px solid var(--border-color)', borderRadius: 6,
  background: '#f9fafb', padding: '0 8px', height: 32,
  boxShadow: 'inset 0 1px 1px rgba(0, 0, 0, 0.03)',
};
const ABS_CONTROL_STYLE = {
  border: 'none', outline: 'none', background: 'transparent',
  fontSize: '0.85rem', fontFamily: 'inherit', color: 'var(--text-primary)',
  padding: 0, cursor: 'pointer',
};
const ABS_DIVIDER_STYLE = { width: 1, height: 14, background: 'var(--border-color)' };

const TOOLTIP_TEXT =
  'Wenn aktiv: Keine Weiterleitung und keine Rückrufe mit Zeitversprechen. Zudem spielt der Assistent die unter „Konfiguration → Anrufablauf-Konfiguration“ eingerichtete „Eigene Begrüßung bei abwesendem Empfang“ ab. Außerhalb der unter „Konfiguration → Praxis-Informationen“ hinterlegten Öffnungs- oder Empfangszeiten gilt dies ohnehin automatisch.';

/**
 * ReceptionStatusWidget
 *
 * Self-contained presence capsule ("Empfang besetzt" / "Abwesend").
 * Uses a zero-layout-shift floating popover for time adjustments so the
 * page below is never pushed down.
 */
const ReceptionStatusWidget = ({ practiceId }) => {
  const queryClient = useQueryClient();
  const containerRef = useRef(null);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [closureBusy, setClosureBusy] = useState(false);
  const [closureError, setClosureError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startHour, setStartHour] = useState('00');
  const [startMin, setStartMin] = useState('00');
  const [endDate, setEndDate] = useState('');
  const [endHour, setEndHour] = useState('00');
  const [endMin, setEndMin] = useState('00');
  const expiryTimerRef = useRef(null);

  const { data: closuresData, error: closuresQueryError } = useQuery(
    ['closures', practiceId],
    async () => {
      const res = await axios.get(
        `${API_BASE_URL}/closures/practice/${practiceId}`,
        { params: { kind: 'reception_absence' } }
      );
      return res.data;
    },
    {
      enabled: !!practiceId,
      staleTime: 15000,
      refetchOnWindowFocus: true,
    }
  );

  const closures = closuresData?.closures || [];
  const boundWindow = closures[0] || null;
  const extraWindows = closures.slice(1);
  const boundIsActive = !!(boundWindow && new Date(boundWindow.starts_at) <= new Date());

  // Click-outside and Escape listener to close floating popover smoothly
  useEffect(() => {
    if (!popoverOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setPopoverOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [popoverOpen]);

  // Sync inputs with bound window
  useEffect(() => {
    if (!boundWindow) {
      setStartDate('');
      setStartHour('00');
      setStartMin('00');
      setEndDate('');
      setEndHour('00');
      setEndMin('00');
      return;
    }
    const s = new Date(boundWindow.starts_at);
    const e = new Date(boundWindow.ends_at);
    setStartDate(isoDay(s));
    setStartHour(pad2(s.getHours()));
    setStartMin(snap5(s.getMinutes()));
    setEndDate(isoDay(e));
    setEndHour(pad2(e.getHours()));
    setEndMin(snap5(e.getMinutes()));
  }, [boundWindow?.id, boundWindow?.starts_at, boundWindow?.ends_at]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expiry timer
  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    if (!boundWindow) return;
    const ms = new Date(boundWindow.ends_at).getTime() - Date.now();
    if (ms < 0 || ms > 24 * 60 * 60 * 1000) return;
    expiryTimerRef.current = setTimeout(() => {
      queryClient.invalidateQueries(['closures', practiceId]);
    }, ms + 2000);
    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
  }, [boundWindow?.id, boundWindow?.ends_at, practiceId, queryClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const runClosure = async (fn) => {
    setClosureBusy(true);
    setClosureError('');
    try {
      await fn();
      await queryClient.invalidateQueries(['closures', practiceId]);
    } catch (err) {
      console.error('Reception-absence action failed:', err);
      setClosureError('Aktion fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setClosureBusy(false);
    }
  };

  const onMasterToggle = (e) => {
    if (closureBusy) return;
    if (e.target.checked) {
      const start = new Date();
      start.setMinutes(Math.floor(start.getMinutes() / 5) * 5, 0, 0);
      const end = new Date(start.getTime() + 60 * 60 * 1000);
      runClosure(() => axios.post(`${API_BASE_URL}/closures/practice/${practiceId}`, {
        starts_at: naiveISO(isoDay(start), pad2(start.getHours()), pad2(start.getMinutes())),
        ends_at: naiveISO(isoDay(end), pad2(end.getHours()), pad2(end.getMinutes())),
      }));
    } else if (boundWindow) {
      setPopoverOpen(false);
      if (boundIsActive) {
        runClosure(() => axios.patch(`${API_BASE_URL}/closures/practice/${practiceId}/${boundWindow.id}`, { end_now: true }));
      } else {
        runClosure(() => axios.delete(`${API_BASE_URL}/closures/practice/${practiceId}/${boundWindow.id}`));
      }
    }
  };

  const startObj = fieldsToDate(startDate, startHour, startMin);
  const endObj = fieldsToDate(endDate, endHour, endMin);
  const editError = (() => {
    if (!boundWindow || !startObj || !endObj) return '';
    if (endObj <= startObj) return 'Endzeit muss nach der Startzeit liegen';
    if (endObj <= new Date()) return 'Endzeit muss in der Zukunft liegen';
    return '';
  })();

  const autoSave = (field, dateStr, hour, min, nextStart, nextEnd) => {
    if (!boundWindow || nextEnd <= nextStart || nextEnd <= new Date()) return;
    runClosure(() =>
      axios.patch(`${API_BASE_URL}/closures/practice/${practiceId}/${boundWindow.id}`, {
        [field]: naiveISO(dateStr, hour, min),
      })
    );
  };

  const onStartChange = (dateStr, hour, min) => {
    setStartDate(dateStr); setStartHour(hour); setStartMin(min);
    if (!endObj) return;
    autoSave('starts_at', dateStr, hour, min, fieldsToDate(dateStr, hour, min), endObj);
  };

  const onEndChange = (dateStr, hour, min) => {
    setEndDate(dateStr); setEndHour(hour); setEndMin(min);
    if (!startObj) return;
    autoSave('ends_at', dateStr, hour, min, startObj, fieldsToDate(dateStr, hour, min));
  };

  const restOfDay = () => {
    if (!boundWindow) return;
    // A running absence ends today, not on its start day (that would be in the past
    // for a window that began yesterday, ending the absence instead of extending it).
    const targetDay = boundIsActive ? isoDay(new Date()) : (startDate || isoDay(new Date(boundWindow.starts_at)));
    runClosure(() =>
      axios.patch(`${API_BASE_URL}/closures/practice/${practiceId}/${boundWindow.id}`, {
        ends_at: naiveISO(targetDay, '23', '55'),
      })
    );
  };

  const deleteClosure = (id) =>
    runClosure(() => axios.delete(`${API_BASE_URL}/closures/practice/${practiceId}/${id}`));

  const absencePillLabel = (() => {
    if (!boundWindow) return '';
    if (boundIsActive) {
      const end = new Date(boundWindow.ends_at);
      const sameDay = isoDay(end) === isoDay(new Date());
      return sameDay
        ? `Abwesend bis ${deTime(end)} Uhr`
        : `Abwesend bis ${deDateShort(end)}, ${deTime(end)} Uhr`;
    }
    const start = new Date(boundWindow.starts_at);
    return `Abwesend ab ${deDateShort(start)}, ${deTime(start)} Uhr`;
  })();

  if (!practiceId) return null;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        display: 'inline-flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span className="dash-head-sep" />

      {/* iOS switch */}
      <label
        className={`ios-switch${closureBusy ? ' is-disabled' : ''}`}
        title={boundWindow ? 'Empfang wieder als besetzt markieren' : 'Empfang als abwesend markieren'}
      >
        <input
          type="checkbox"
          checked={!!boundWindow}
          disabled={closureBusy}
          onChange={onMasterToggle}
          aria-label="Empfang abwesend"
        />
        <span className="ios-switch-slider" />
      </label>

      {/* Pill: Static green pill when besetzt; interactive clickable trigger when away */}
      {boundWindow ? (
        <button
          type="button"
          onClick={() => setPopoverOpen((prev) => !prev)}
          className="dash-state dash-state--away"
          title="Klick: Abwesenheit anpassen"
          aria-expanded={popoverOpen}
          style={{
            height: 28,
            background: 'color-mix(in srgb, var(--color-tertiary) 8%, #fff)',
            border: '1px solid color-mix(in srgb, var(--color-tertiary) 32%, transparent)',
            borderRadius: 6,
            padding: '0 10px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: 'inherit',
            fontSize: '0.82rem',
            fontWeight: 600,
            color: 'var(--color-tertiary)',
            lineHeight: 'normal',
            transition: 'all 0.15s ease',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'color-mix(in srgb, var(--color-tertiary) 14%, #fff)';
            e.currentTarget.style.borderColor = 'var(--color-tertiary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'color-mix(in srgb, var(--color-tertiary) 8%, #fff)';
            e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--color-tertiary) 32%, transparent)';
          }}
        >
          <i className="dash-state-dot" />
          <span>{absencePillLabel}</span>
          <svg
            width="9"
            height="9"
            viewBox="0 0 12 12"
            style={{
              transform: popoverOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s ease',
              color: 'var(--color-tertiary)',
              opacity: 0.85,
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      ) : (
        <span className="dash-state">
          <i className="dash-state-dot" />
          Empfang besetzt
        </span>
      )}

      {/* Header Quick Action: Sleek pill button for "Rest des Tages" */}
      {boundWindow && (
        <button
          type="button"
          disabled={closureBusy}
          onClick={restOfDay}
          title="Empfang bis 23:55 Uhr als abwesend markieren"
          style={{
            height: 28,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '0 10px',
            background: 'color-mix(in srgb, var(--color-secondary) 8%, #fff)',
            border: '1px solid color-mix(in srgb, var(--color-secondary) 25%, transparent)',
            borderRadius: 6,
            color: 'var(--color-secondary)',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: closureBusy ? 'not-allowed' : 'pointer',
            opacity: closureBusy ? 0.6 : 1,
            whiteSpace: 'nowrap',
            transition: 'all 0.15s ease',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
            margin: 0,
          }}
          onMouseEnter={(e) => {
            if (closureBusy) return;
            e.currentTarget.style.background = 'color-mix(in srgb, var(--color-secondary) 14%, #fff)';
            e.currentTarget.style.borderColor = 'var(--color-secondary)';
            e.currentTarget.style.transform = 'translateY(-0.5px)';
            e.currentTarget.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.08)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'color-mix(in srgb, var(--color-secondary) 8%, #fff)';
            e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--color-secondary) 25%, transparent)';
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.03)';
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
          Rest des Tages
        </button>
      )}

      {/* Info Tooltip */}
      <span
        className="th-info-icon"
        tabIndex={0}
        role="img"
        aria-label="Erklärung zum Empfangsstatus"
        data-info-tooltip={TOOLTIP_TEXT}
        style={{ margin: 0 }}
      >
        ⓘ
      </span>

      {/* Query / Action Error: next to the switch, not in the popover, which is closed
          (or has no window to show) whenever a toggle or the load fails */}
      {(closureError || closuresQueryError) && (
        <span style={{ fontSize: '0.8rem', color: 'var(--color-tertiary)' }}>
          {closureError || 'Abwesenheiten konnten nicht geladen werden.'}
        </span>
      )}

      {/* Floating Popover: Zero layout shift — floats above page content anchored beneath the pill */}
      {popoverOpen && boundWindow && (
        <div
          role="dialog"
          aria-label="Abwesenheit bearbeiten"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 12,
            zIndex: 1000,
            width: 340,
            maxWidth: 'calc(100vw - 32px)',
            background: '#fff',
            border: '1px solid var(--border-color)',
            borderRadius: 10,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.06)',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {/* Popover Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="dash-state-dot" style={{ background: 'var(--color-tertiary)' }} />
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                Abwesenheit anpassen
              </span>
            </div>
            <button
              type="button"
              onClick={() => setPopoverOpen(false)}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                padding: '2px 4px',
                fontSize: '0.9rem',
                lineHeight: 1,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label="Schließen"
            >
              ✕
            </button>
          </div>

          {/* Time pickers: Von & Bis */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={ABS_LABEL_STYLE}>Von:</span>
              <div style={ABS_GROUP_STYLE}>
                <input
                  type="date"
                  value={startDate}
                  disabled={closureBusy}
                  onChange={(e) => onStartChange(e.target.value, startHour, startMin)}
                  style={{ ...ABS_CONTROL_STYLE, width: 114 }}
                />
                <span style={ABS_DIVIDER_STYLE} />
                <select style={ABS_CONTROL_STYLE} value={startHour} disabled={closureBusy} onChange={(e) => onStartChange(startDate, e.target.value, startMin)}>
                  {HOURS_24.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                <span style={{ color: 'var(--text-secondary)' }}>:</span>
                <select style={ABS_CONTROL_STYLE} value={startMin} disabled={closureBusy} onChange={(e) => onStartChange(startDate, startHour, e.target.value)}>
                  {MINUTES_5.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={ABS_LABEL_STYLE}>Bis:</span>
              <div style={ABS_GROUP_STYLE}>
                <input
                  type="date"
                  value={endDate}
                  disabled={closureBusy}
                  onChange={(e) => onEndChange(e.target.value, endHour, endMin)}
                  style={{ ...ABS_CONTROL_STYLE, width: 114 }}
                />
                <span style={ABS_DIVIDER_STYLE} />
                <select style={ABS_CONTROL_STYLE} value={endHour} disabled={closureBusy} onChange={(e) => onEndChange(endDate, e.target.value, endMin)}>
                  {HOURS_24.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                <span style={{ color: 'var(--text-secondary)' }}>:</span>
                <select style={ABS_CONTROL_STYLE} value={endMin} disabled={closureBusy} onChange={(e) => onEndChange(endDate, endHour, e.target.value)}>
                  {MINUTES_5.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Validation error */}
          {editError && (
            <span style={{ fontSize: '0.8rem', color: 'var(--color-tertiary)' }}>
              {editError}
            </span>
          )}

          {/* Dedicated Full-Width Action in Popover */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 10, borderTop: '1px solid var(--border-light)' }}>
            <button
              type="button"
              disabled={closureBusy}
              onClick={restOfDay}
              style={{
                width: '100%',
                height: 34,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                background: 'color-mix(in srgb, var(--color-secondary) 8%, #fff)',
                border: '1px solid color-mix(in srgb, var(--color-secondary) 25%, transparent)',
                borderRadius: 6,
                color: 'var(--color-secondary)',
                fontSize: '0.82rem',
                fontWeight: 600,
                cursor: closureBusy ? 'not-allowed' : 'pointer',
                opacity: closureBusy ? 0.6 : 1,
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (closureBusy) return;
                e.currentTarget.style.background = 'color-mix(in srgb, var(--color-secondary) 14%, #fff)';
                e.currentTarget.style.borderColor = 'var(--color-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'color-mix(in srgb, var(--color-secondary) 8%, #fff)';
                e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--color-secondary) 25%, transparent)';
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
              Rest des Tages (bis 23:55 Uhr)
            </button>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, color: 'var(--text-secondary)', fontSize: '0.74rem' }}>
              <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor" style={{ opacity: 0.6 }}>
                <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
              </svg>
              <span>Änderungen werden automatisch gespeichert</span>
            </div>
          </div>

          {/* Extra future windows (safety rail) */}
          {extraWindows.length > 0 && (
            <div className="dash-rail-extras">
              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                Weitere geplante Abwesenheiten:
              </span>
              {extraWindows.map((c) => {
                const start = new Date(c.starts_at);
                const end = new Date(c.ends_at);
                return (
                  <div key={c.id} className="dash-rail-extra">
                    <span>{deDateShort(start)}, {deTime(start)} – {deTime(end)} Uhr</span>
                    <button type="button" className="delete-button" disabled={closureBusy} onClick={() => deleteClosure(c.id)}>
                      Löschen
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReceptionStatusWidget;
